// External packages
import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { z } from 'zod';

// Components
import { ButtonIcon } from 'components/Button';
import { Checkbox } from 'components/Checkbox';
import { Icon } from 'components/Icons';
import { SearchField } from 'components/SearchField';
import { Option, Select } from 'components/Select';
import { Tab, TabList, TabPanel, Tabs } from 'components/Tabs';

// Modules
import { BulkActionsToolbar } from 'modules/BulkActionsToolbar';
import { ExhibitGallery } from 'modules/exhibits/ExhibitGallery';
import { ExhibitsModal } from 'modules/exhibits/ExhibitsModal';

// Hooks
import {
  useDeleteExhibit,
  useGetExhibits,
  useUpdateExhibit,
} from 'api/exhibit';
import { useGetPlaces } from 'api/place';
import { useBulkSelection } from 'hooks/useBulkSelection';
import { useTranslationSelection } from 'hooks/useTranslationSelection';

// Types
import { Exhibit } from '../../../../server/src/types/exhibit.types';

// Utils
import { filterExhibits } from 'utils/filterExhibit';
import { sortExhibits } from 'utils/sortExhibit';

// Schemas
import { exhibitsCreateSchema } from 'schemas/exhibit';

export const ExhibitsList: React.FC<{ selectedExhibitId?: string }> = ({
  selectedExhibitId,
}) => {
  const [search, setSearch] = React.useState('');
  const [sortBy, setSortBy] = React.useState<string>('datum');
  const [modalOpen, setModalOpen] = React.useState(false);
  const [wrapperWidth, setWrapperWidth] = React.useState(0);
  const [activeTab, setActiveTab] = React.useState<string>('');

  const { data: exhibits, isLoading, isError } = useGetExhibits();
  const { data: places = [] } = useGetPlaces();
  const { mutate: deleteExhibit, isPending: isDeleting } = useDeleteExhibit();

  const wrapperRef = React.useRef<HTMLDivElement>(null);

  const filteredExhibits = React.useMemo(
    () => filterExhibits(exhibits, search),
    [exhibits, search]
  );

  const sortedExhibits = React.useMemo(
    () => sortExhibits(filteredExhibits, sortBy),
    [filteredExhibits, sortBy]
  );

  const exhibitsByPlace = React.useMemo(() => {
    const map: Record<string, Exhibit[]> = {};
    sortedExhibits.forEach((exhibit) => {
      const key = exhibit.placeId || 'no-place';
      if (!map[key]) map[key] = [];
      map[key].push(exhibit);
    });
    return map;
  }, [sortedExhibits]);

  const currentExhibits = exhibitsByPlace[activeTab] || [];
  const bulkSelection = useBulkSelection(currentExhibits);

  const isBulkActionActive = bulkSelection.selectedIds.length > 0;

  React.useEffect(() => {
    if (!activeTab) {
      const firstPlaceWithExhibits = places.find(
        (place) =>
          exhibitsByPlace[place.id] && exhibitsByPlace[place.id].length > 0
      );
      if (firstPlaceWithExhibits) {
        setActiveTab(firstPlaceWithExhibits.id);
      } else if (
        exhibitsByPlace['no-place'] &&
        exhibitsByPlace['no-place'].length > 0
      ) {
        setActiveTab('no-place');
      }
    }
  }, [places, exhibitsByPlace, activeTab]);

  React.useEffect(() => {
    if (wrapperRef.current) {
      const width = wrapperRef.current.offsetWidth;
      setWrapperWidth(width);
    }
  }, [wrapperRef]);

  React.useEffect(() => {
    if (!selectedExhibitId || !exhibits) return;
    const exhibit = exhibits.find((e) => e.id === selectedExhibitId);

    if (exhibit) {
      setActiveTab(exhibit.placeId || 'no-place');
    }
  }, [selectedExhibitId, exhibits]);

  return (
    <div className="flex flex-col h-full" ref={wrapperRef}>
      {exhibits && exhibits.length > 0 && (
        <>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <SearchField
              value={search}
              onChange={setSearch}
              placeholder="Pretraži eskponate..."
            />
            <Select
              placeholder="Sortiraj po"
              className="w-auto"
              selectedKey={sortBy}
              onSelectionChange={(key) => setSortBy(key as string)}
            >
              <Option id="naziv">Naziv (A-B)</Option>
              <Option id="naziv-desc">Naziv (Z-A)</Option>
              <Option id="datum">Dodano (najnovije)</Option>
              <Option id="datum-desc">Dodano (najstarije)</Option>
            </Select>
          </div>

          <BulkActionsToolbar
            allSelected={!!bulkSelection.allSelected}
            someSelected={!!bulkSelection.someSelected}
            selectedCount={bulkSelection.selectedIds.length}
            onSelectAll={bulkSelection.handleSelectAll}
            onBulkDelete={() => {
              bulkSelection.selectedIds.forEach((id) => deleteExhibit({ id }));
              bulkSelection.clearSelection();
              setModalOpen(false);
            }}
            isDeleteModalOpen={modalOpen}
            setDeleteModalOpen={setModalOpen}
            isDeleting={isDeleting}
            label="Odaberi sve"
          />
        </>
      )}

      {isLoading ||
      isError ||
      !sortedExhibits ||
      sortedExhibits.length === 0 ? (
        <div className="flex items-center justify-center h-full">
          {isLoading ? (
            <Icon name="loader" className="animate-spin size-10" />
          ) : isError ? (
            <p>Greška pri učitavanju eksponata.</p>
          ) : (
            <p>Nema pronađenih eksponata.</p>
          )}
        </div>
      ) : (
        <Tabs
          selectedKey={activeTab}
          onSelectionChange={(key) => setActiveTab(key as string)}
          className="mt-4"
        >
          <TabList style={{ width: wrapperWidth }} className="overflow-x-auto">
            {places.map((place) => (
              <Tab
                key={place.id}
                id={place.id}
                isDisabled={
                  isBulkActionActive ||
                  !exhibitsByPlace[place.id] ||
                  exhibitsByPlace[place.id].length === 0
                }
                className="flex-1 text-center"
              >
                {place.translations[0]?.title || place.name || 'Nepoznata soba'}
              </Tab>
            ))}
            {exhibitsByPlace['no-place'] && (
              <Tab
                key="no-place"
                id="no-place"
                isDisabled={
                  isBulkActionActive || exhibitsByPlace['no-place'].length === 0
                }
                className="flex-1 text-center"
              >
                Bez sobe
              </Tab>
            )}
          </TabList>
          {places.map((place) => (
            <TabPanel key={place.id} id={place.id} className="p-4">
              {exhibitsByPlace[place.id] &&
                exhibitsByPlace[place.id].length > 0 && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {exhibitsByPlace[place.id].map((exhibit) => (
                      <div key={exhibit.id}>
                        <Checkbox
                          isSelected={
                            activeTab === place.id &&
                            bulkSelection.selectedIds.includes(exhibit.id)
                          }
                          onChange={(checked) =>
                            activeTab === place.id &&
                            bulkSelection.handleSelect(exhibit.id, !!checked)
                          }
                        />
                        <ExhibitListItem
                          exhibit={exhibit}
                          selectedExhibitId={selectedExhibitId}
                        />
                      </div>
                    ))}
                  </div>
                )}
            </TabPanel>
          ))}
          {exhibitsByPlace['no-place'] && (
            <TabPanel key="no-place" id="no-place" className="p-4">
              {exhibitsByPlace['no-place'].length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {exhibitsByPlace['no-place'].map((exhibit) => (
                    <div key={exhibit.id}>
                      <Checkbox
                        isSelected={
                          activeTab === 'no-place' &&
                          bulkSelection.selectedIds.includes(exhibit.id)
                        }
                        onChange={(checked) =>
                          activeTab === 'no-place' &&
                          bulkSelection.handleSelect(exhibit.id, !!checked)
                        }
                      />
                      <ExhibitListItem
                        exhibit={exhibit}
                        selectedExhibitId={selectedExhibitId}
                      />
                    </div>
                  ))}
                </div>
              )}
            </TabPanel>
          )}
        </Tabs>
      )}
    </div>
  );
};

export const ExhibitListItem: React.FC<{
  exhibit: Exhibit;
  selectedExhibitId?: string;
}> = ({ exhibit, selectedExhibitId }) => {
  const [editModalOpen, setEditModalOpen] = React.useState(false);
  const navigate = useNavigate();
  const pathName = useLocation();

  const { mutate: updateExhibit, isPending: isUpdating } = useUpdateExhibit();
  const translationSelection = useTranslationSelection(exhibit.translations);

  const handleUpdate = (data: z.infer<typeof exhibitsCreateSchema>) => {
    updateExhibit(
      { id: exhibit.id, data },
      {
        onSuccess: () => {
          setEditModalOpen(false);
        },
      }
    );
  };

  React.useEffect(() => {
    if (selectedExhibitId === exhibit.id) {
      setEditModalOpen(true);

      navigate(
        {
          pathname: pathName.pathname.split('/eksponati')[0] + '/eksponati',
        },
        { replace: true }
      );
    }
  }, [selectedExhibitId, exhibit.id, navigate]);

  return (
    <div
      key={exhibit.id}
      className="border bg-white flex flex-col col-span-1 max-h-100 border-gray-300 overflow-hidden rounded-lg shadow-xs"
    >
      <ExhibitGallery
        images={exhibit.images}
        altBase={translationSelection.translation.title}
      />
      <div className="p-4 flex h-full flex-col" style={{ overflow: 'hidden' }}>
        <h3 className="text-sm text-gray-400">{exhibit.name}</h3>
        <h2 className="text-lg font-semibold">
          {translationSelection.translation.title}
        </h2>
        <p
          className="text-gray-600 mb-2 truncate"
          dangerouslySetInnerHTML={{
            __html:
              translationSelection.translation.shortDescription ||
              translationSelection.translation.description ||
              '',
          }}
        />
        <div className="mt-auto flex gap-4 justify-between flex-wrap items-center">
          <ExhibitsModal
            onSubmit={handleUpdate}
            defaultValues={{
              name: exhibit.name || '',
              translations: exhibit.translations.map((t) => ({
                languageCode: t.languageCode,
                title: t.title,
                description: t.description,
                shortDescription: t.shortDescription || '',
                videoIds: t.videoIds || [],
                audioIds: t.audioIds || [],
              })),
              imageIds: exhibit.imageIds || [],
              placeId: exhibit.placeId || '',
              position: exhibit.position || { x: 0, y: 0 },
            }}
            modalTrigger={
              <ButtonIcon variant="outline">
                <Icon name="edit" />
              </ButtonIcon>
            }
            isPending={isUpdating}
            isOpen={editModalOpen}
            onOpenChange={setEditModalOpen}
          />
          <Select
            selectedKey={translationSelection.selectedLanguage}
            onSelectionChange={(key) =>
              translationSelection.setSelectedLanguage(key as string)
            }
            items={translationSelection.languageOptions}
          >
            {translationSelection.languageOptions.map((item) => (
              <Option key={item.key} id={item.key}>
                {item.label}
              </Option>
            ))}
          </Select>
        </div>
      </div>
    </div>
  );
};
